<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="t('Estornar Pagamento')"
    :steps="wizardSteps"
    :step-data="formData"
    :loading="isLoading"
    size="compactDesktop"
    orientation="vertical"
    :show-step-description="true"
    :reset-on-close="true"
    :allow-step-click="false"
    @cancel="handleClose"
    @close="handleClose"
    @finish="handleFinish"
  >
    <!-- Step 0: Amount & Reason -->
    <template #step-0>
      <RefundStepAmount
        v-model="formData"
        :recebimento="recebimento"
      />
    </template>

    <!-- Step 1: Confirmation -->
    <template #step-1>
      <RefundStepConfirm
        v-model="formData"
        :recebimento="recebimento"
      />
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '../../ui/WizardModal.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useRecebimento } from '../../composables/useRecebimento';
import { permissionsService, permissionsState } from '../../services/permissionsService';
import { currencyMinorUnitsFactor } from '../../utils/currencyMinorUnits';

// Step components
import RefundStepAmount from './wizard/RefundStepAmount.vue';
import RefundStepConfirm from './wizard/RefundStepConfirm.vue';

interface RefundFormData {
  amount: number;
  description: string;
  confirmed: boolean;
  [key: string]: unknown;
}

interface RecebimentoSource {
  recebimento_id?: number;
  recebimento_valor?: number;
  recebimento_id_externo?: string;
  recebimento_origem_id?: number | string;
  [key: string]: unknown;
}

interface RefundOptions {
  value: number;
  description?: string;
}

interface WizardStep {
  title: string;
  description: string;
  icon: string;
  validate: () => boolean;
}

interface OpenPayload {
  recebimento?: RecebimentoSource;
}

const { t } = useI18n();
const { refundRecebimento, canRefundRecebimento, hasPendingRefund } = useRecebimento();

// State
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const recebimento = ref<RecebimentoSource>({});

// Price factor derived from active locale (100 for BRL, 1 for CLP, etc.)
const priceFactor = currencyMinorUnitsFactor((permissionsState as { normalizedLocale?: string })?.normalizedLocale);


// Form data
const formData = ref<RefundFormData>({
  amount: 0,        // Amount in centavos
  description: '',  // Refund reason (required)
  confirmed: false  // User confirmation checkbox
});

// Step 1 validation: amount must be > 0 and <= original amount, and reason is required
const isStep1Valid = computed<boolean>(() => {
  const amount = formData.value.amount || 0;
  const originalAmount = recebimento.value.recebimento_valor || 0;
  const description = (formData.value.description || '').trim();
  return amount > 0 && amount <= originalAmount && description.length > 0;
});

// Step 2 validation: user must confirm
const isStep2Valid = computed<boolean>(() => {
  return formData.value.confirmed === true && formData.value.amount > 0;
});

// Wizard step configuration
const wizardSteps = computed<WizardStep[]>(() => [
  {
    title: t('Valor'),
    description: t('Defina o valor do estorno'),
    icon: 'pi pi-dollar',
    validate: () => isStep1Valid.value
  },
  {
    title: t('Confirmação'),
    description: t('Revise e confirme'),
    icon: 'pi pi-check-circle',
    validate: () => isStep2Valid.value
  }
]);

// Methods
const resetForm = (): void => {
  // Pre-fill with full amount, reset confirmation
  formData.value = {
    amount: recebimento.value.recebimento_valor || 0,
    description: '',
    confirmed: false
  };
};

const handleFinish = async (): Promise<void> => {
  log('RefundWizard', 'handleFinish', 'Processing refund', {
    recebimento_id: recebimento.value.recebimento_id,
    amount: formData.value.amount,
    description: formData.value.description
  });

  isLoading.value = true;

  try {
    // Convert amount from minor units to display value for the API
    const refundOptions: RefundOptions = {
      value: formData.value.amount / priceFactor
    };

    if (formData.value.description) {
      refundOptions.description = formData.value.description;
    }

    const result = await refundRecebimento(
      recebimento.value.recebimento_id,
      refundOptions
    );

    log('RefundWizard', 'handleFinish', 'Refund successful', result);

    eventBus.emit('globalMessage.success', t('Pagamento estornado com sucesso!'));

    // Emit refresh events
    if (recebimento.value.recebimento_origem_id) {
      eventBus.emit(`refresh-recebimento-${recebimento.value.recebimento_origem_id}`);
    }
    eventBus.emit('recebimento.refresh');
    eventBus.emit('recebimento.refunded', {
      recebimento_id: recebimento.value.recebimento_id,
      amount: formData.value.amount,
      result
    });
    eventBus.emit('compromisso.refresh');

    // Close wizard
    handleClose();

  } catch (error) {
    log('RefundWizard', 'handleFinish', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao estornar pagamento') + ': ' + message);
  } finally {
    isLoading.value = false;
  }
};

const handleClose = (): void => {
  log('RefundWizard', 'handleClose', 'Closing wizard');
  isVisible.value = false;
  recebimento.value = {};
  resetForm();
};

/**
 * Open the refund wizard
 */
const handleOpen = async (data?: OpenPayload): Promise<void> => {
  log('RefundWizard', 'handleOpen', 'Opening wizard with data', {
    hasData: !!data,
    hasRecebimento: !!data?.recebimento,
    recebimento_id: data?.recebimento?.recebimento_id,
    recebimento_id_externo: data?.recebimento?.recebimento_id_externo,
    recebimento_id_externo_type: typeof data?.recebimento?.recebimento_id_externo,
    recebimento_valor: data?.recebimento?.recebimento_valor
  });

  // Permission check - Only Full, Franqueado, and Gerente can refund
  const hasRefundPermission = permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']);
  if (!hasRefundPermission) {
    log('RefundWizard', 'handleOpen', 'Permission denied: User does not have refund permission');
    eventBus.emit('globalMessage.error', t('Sem permissão para estornar pagamentos. Apenas administradores, franqueados e gerentes podem realizar estornos.'));
    return;
  }

  if (!data?.recebimento) {
    log('RefundWizard', 'handleOpen', 'Error: No recebimento provided');
    eventBus.emit('globalMessage.error', t('Erro: Dados do recebimento nao fornecidos'));
    return;
  }

  // Use the same validation helper used by RecebimentoModal
  if (!canRefundRecebimento(data.recebimento)) {
    log('RefundWizard', 'handleOpen', 'Error: Recebimento cannot be refunded', {
      recebimento_id_externo: data.recebimento.recebimento_id_externo,
      canRefund: canRefundRecebimento(data.recebimento)
    });
    eventBus.emit('globalMessage.error', t('Este recebimento não pode ser estornado (sem ID de cobrança do provedor)'));
    return;
  }

  // Check for pending refund request
  try {
    isLoading.value = true;
    const hasPending = await hasPendingRefund(data.recebimento.recebimento_id ?? 0);

    if (hasPending) {
      log('RefundWizard', 'handleOpen', 'Blocked: Pending refund exists', {
        recebimento_id: data.recebimento.recebimento_id
      });
      eventBus.emit('globalMessage.warning', t('Já existe um estorno pendente para este recebimento. Aguarde a confirmação do provedor.'));
      return;
    }
  } catch (error) {
    log('RefundWizard', 'handleOpen', 'Error checking pending refund', error);
    // Continue with opening - let the backend handle the duplicate check
  } finally {
    isLoading.value = false;
  }

  // Store recebimento data
  recebimento.value = { ...data.recebimento };

  // Reset form (includes resetting confirmation)
  resetForm();

  isVisible.value = true;
};

// Lifecycle
onMounted(() => {
  log('RefundWizard', 'onMounted', 'Component mounted');

  // Listen for wizard events
  eventBus.on('modal.refund-wizard.open', handleOpen);
  eventBus.on('modal.refund-wizard.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.refund-wizard.open', handleOpen);
  eventBus.off('modal.refund-wizard.close', handleClose);
});

// Expose methods
defineExpose({
  open: handleOpen,
  close: handleClose
});
</script>
